import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAssignCampaignTeamOrAgents from '@hooks/useAssignCampaignTeamOrAgents'
import { useCampaignForm } from '@hooks/useCampaign'

import AgentOrTeamSearch from '@components/AgentOrTeamSearch'
import SelectedAgentsAndTeam from '@components/SelectedAgentsAndTeam'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

const MODAL_KEY = 'AssignCampaignAgentsModal'

const customRequiredFields = [
  { key: 'country_code' },
  { key: 'post_code' },
  { key: 'state_name' },
]

type ModalCallbacks = {
  closeModal: () => void,
  updateCampaign: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const AssignCampaignAgentsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateCampaign } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign: initCampaign } = modalPayload

  const campaignFormPayload = useCampaignForm(initCampaign, { customRequiredFields })

  const assignCampaignTeamOrAgentsPayload = useAssignCampaignTeamOrAgents({
    campaign: initCampaign,
    campaignFormPayload,
  })
  const {
    callbacks: {
      selectAgentOrTeam,
    },
    campaignPayload,
  } = assignCampaignTeamOrAgentsPayload

  const {
    callbacks: { updateCampaign: updateFn },
    updating,
  } = campaignPayload

  const {
    entityState,
  } = campaignFormPayload

  const agentIds = digObject(entityState, 'agent_ids', [])

  const updateCampaignPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: { ...entityState, agents: agentIds },
    requestOptions: defaultRequestOptions.campaign,
    toastText: 'Team Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Campaign" titleSecondLine="Agents" />

      <SidebarModal.Content>
        <Form.Field label="Assign Team & Agents">
          <AgentOrTeamSearch
            callbacks={{ selectAgentOrTeam }}
            selectedAgentIds={entityState.agent_ids || []}
            selectedTeamId={entityState.team_id}
          />
        </Form.Field>

        <SelectedAgentsAndTeam assignCampaignTeamOrAgentsPayload={assignCampaignTeamOrAgentsPayload} />
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!agentIds?.length}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => updateCampaign(updateCampaignPayload)}
          size="large"
        >
          {agentIds?.length ? 'Save Agents' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <AssignCampaignAgentsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
